import { get, post, put, patch, del } from "./client";
import { ApiEndpoint, ApiSubresourceEndpoint } from "./endpoints";
import { getUnixTimestamp, setDateToMidnight } from "../util/date-utility";
import { dispatchSubresourceRequest, Method } from "./dispatcher";
import { Payment } from './payments';
import { isAfter } from 'date-fns';

export interface InquiryMessage {
	date: string;
	message: string;
	userId: string;
}

export interface Inquiry {
	id: string;
	area: string;
	date: Date;
	peopleCount: number;
	primaryName: string;
	time: Date;
	phoneNumber: string;
	email: string;
	notes: string;
	meals?: { [key: string]: number };
	created: Date;
	accepted: Boolean;
	rejected: Boolean;
	deleted: Boolean;
	snoozed: Boolean;
	replied: Boolean;
	messages: InquiryMessage[];
	payment?: Payment;
}

export const transformInquiryResponse = <T extends Partial<Inquiry>>(
	data: T
): T => {
	if (typeof data.date === "string") {
		data.date = new Date(data.date);
	}

	if (typeof data.created === "string") {
		data.created = new Date(data.created);
	}

	if (data.time && data.time.toString().substr(0, 4) === "0001") {
		data.time = null;
	} else if (typeof data.time === "string") {
		data.time = new Date(data.time);
	}

	if (data.payment) {
		if (
			data.payment.state === "awaiting_payment"
			&& isAfter(new Date(), new Date(data.payment.expirationDate))
		) {
			data.payment.state = "expired";
		}
	}

	if (!data.messages) {
		data.messages = [];
	}

	return data;
};

export const getInquiries = async (): Promise<Inquiry[]> => {
	const r = (await get(ApiEndpoint.Inquiries)) as [Inquiry[]];

	return r.reduce((acc, result) => {
		return acc.concat(result.map(res => transformInquiryResponse(res)));
	}, []);
};

export const getInquiryById = async (id: string): Promise<Inquiry> => {
	const r = (await get(ApiEndpoint.Inquiries, id)) as Inquiry[];

	return transformInquiryResponse(r[0]);
};

export const getInquiriesByDateRange = async (
	start: Date,
	end: Date
): Promise<Inquiry[]> => {
	start.setHours(0, 0, 0, 0);
	end.setHours(23, 59, 59, 999);

	const r = (await get(ApiEndpoint.Inquiries, null, {
		from: getUnixTimestamp(start),
		to: getUnixTimestamp(end)
	})) as [Inquiry[]];

	return r.reduce((acc, result) => {
		return acc.concat(result.map(res => transformInquiryResponse(res)));
	}, []);
};

export const patchInquiry = async (
	id: string,
	updateData: Partial<Inquiry>
) => {
	const r = (await patch(ApiEndpoint.Inquiries, id, updateData)) as Inquiry;

	return transformInquiryResponse(r);
};

export const addInquiryMessage = async (inquiryId: string, message: string) => {
	const r = await dispatchSubresourceRequest(
		ApiEndpoint.Inquiries,
		ApiSubresourceEndpoint.InquiryMessages,
		Method.POST,
		inquiryId,
		null,
		{
			message
		}
	);

	return r;
};
